import type {
  CachedBindings,
  PartialEvaluation,
  QueryResolver,
} from '@jetfabric/conditional-expressions/lib/data-reference-resolver';
import { isLeft, isRight, left, right } from '@jetfabric/conditional-expressions/lib/either';
import { isDataReference } from '@jetfabric/conditional-expressions/lib/guards';
import {
  createTraceQueryContext,
  getTraceTimestamp,
  tracingEnabled,
} from '@jetfabric/conditional-expressions/lib/trace-utils';
import { ERROR_CODES } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/error';
import {
  EventProductView,
  V2_EVENT_TYPES,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/events';
import { V2Request } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/protocol';
import type { ConditionalExpression } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/condition';
import {
  DataReference,
  Literal,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/data-reference';
import {
  QueryClientEvent,
  QueryClientState,
  QueryEvaluateCondition,
  QueryExecutionResult,
  QueryGraphQL,
  QueryPreviouslyViewedProducts,
  V2Query,
  V2_QUERY_TYPES,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/queries';
import {
  ExecutionEnvironment,
  TraceQueryContext,
  TraceState,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/trace';
import { ProductListResult } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/results';
import { inject, injectable, optional } from 'inversify';
import { ProductView } from '../api-response';
import { EVENT_TYPES, ProductViewEvent } from '../client-events';
import { DI_TYPES } from '../di-types';
import { BoundedEventStore } from '../event-store';
import { ClientEventDBProvider } from '../util/client-event-db-provider';
import type { ConditionalExpressionProvider } from '../util/conditional-expressions-provider';
import { ClientEventDB } from './client-event-db/client-event-db';
import { ClientStateRoot } from './client-state/schema';

type V2ProcessorBase = {
  [key in V2_QUERY_TYPES]?: (
    clientState: ClientStateRoot,
    query: V2Query,
    fullRequest: V2Request,
    bindingsStack: CachedBindings[],
    traceState?: TraceState
  ) => Promise<QueryExecutionResult>;
};

@injectable()
export class V2ClientSideRequestProcessor implements V2ProcessorBase {
  // Note: the eventStore uses the v1 event types, conversions between the two happens here.
  private eventStore: BoundedEventStore;
  private clientEventDB?: ClientEventDB;
  private clientEventDBProvider?: ClientEventDBProvider;
  private conditionalExpressionProvider: ConditionalExpressionProvider;

  private getQueryResolver(clientState: ClientStateRoot, fullRequest: V2Request): QueryResolver {
    return async (
      queryToResolve: V2Query,
      bindingsStack: CachedBindings[],
      traceState?: TraceState
    ) => {
      const result = await this.processQuery(
        clientState,
        queryToResolve,
        fullRequest,
        bindingsStack,
        traceState
      );
      if ('result' in result) {
        return right(result.result);
      } else if ('partialResult' in result) {
        return left(result.partialResult);
      } else if ('error' in result && result.error) {
        throw new Error(result.error.message);
      }
      // this shouldn't happen
      throw new Error("couldn't process result");
    };
  }

  private async evaluate(
    conditionalExpression: ConditionalExpression,
    clientState: ClientStateRoot,
    fullRequest: V2Request,
    bindingsStack: CachedBindings[] = [],
    traceState?: TraceState
  ): PartialEvaluation {
    return await (await this.conditionalExpressionProvider())(
      conditionalExpression,
      this.getQueryResolver(clientState, fullRequest),
      bindingsStack,
      traceState
    );
  }

  constructor(
    @inject(DI_TYPES.BoundedEventStore) eventStore: BoundedEventStore,
    @inject(DI_TYPES.ConditionaExpressionProvider)
    conditionalExpressionProvider: ConditionalExpressionProvider,
    @inject(DI_TYPES.ClientEventDBProvider)
    @optional()
    clientEventDBProvider?: ClientEventDBProvider
  ) {
    this.eventStore = eventStore;
    this.conditionalExpressionProvider = conditionalExpressionProvider;
    this.clientEventDBProvider = clientEventDBProvider;
  }

  [V2_QUERY_TYPES.PREVIOUSLY_VIEWED_PRODUCTS](
    clientState: ClientStateRoot,
    query: V2Query,
    fullRequest: V2Request,
    bindingsStack: CachedBindings[]
  ): Promise<QueryExecutionResult> {
    const q: QueryPreviouslyViewedProducts = query as QueryPreviouslyViewedProducts;
    // can't pass the limit to getEvents because filterCurrent and mergeVariants
    // may remove product views; instead limit is enforced after filtering.
    let productViews: ProductView[] = ([...this.eventStore.getEvents()].filter(
      ({ type }) => type === EVENT_TYPES.PRODUCT_VIEW
    ) as ProductViewEvent[]) // sry, temporary workaround [peter.jozsa]
      .map(({ timestamp, attributes: { product } }) => ({
        timestamp,
        product,
      }));
    if (q?.filterCurrent) {
      productViews = V2ClientSideRequestProcessor.filterCurrent(productViews, fullRequest);
    }
    if (q?.mergeVariants) {
      productViews = V2ClientSideRequestProcessor.mergeVariants(productViews);
    }
    if (typeof q?.limit === 'number' && q.limit > 0) {
      productViews = productViews.slice(0, q.limit);
    }
    return Promise.resolve({
      result: {
        products: productViews.map(({ product: { productId, variantId } }) => ({
          productId,
          variantId,
        })),
      } as ProductListResult,
    });
  }

  [V2_QUERY_TYPES.CLIENT_STATE](
    clientState: ClientStateRoot,
    query: V2Query,
    _fullRequest: V2Request,
    _bindingsStack: CachedBindings[]
  ): Promise<QueryExecutionResult> {
    const q = query as QueryClientState;
    return Promise.resolve({
      result: clientState,
    });
  }

  async [V2_QUERY_TYPES.CLIENT_EVENT](
    clientState: ClientStateRoot,
    query: V2Query,
    fullRequest: V2Request,
    bindingsStack: CachedBindings[],
    traceState?: TraceState
  ) {
    const q = query as QueryClientEvent;
    // Client events isn't necessarily present in the inversifyJS container.
    // If we can't find it, we return null as the query result.
    if (!this.clientEventDBProvider) {
      return { result: null };
    }
    if (!this.clientEventDB) {
      this.clientEventDB = await this.clientEventDBProvider();
    }
    const result = await this.clientEventDB.query(
      q,
      this.getQueryResolver(clientState, fullRequest),
      bindingsStack,
      traceState
    );
    if (isLeft(result)) {
      return { partialResult: result.value };
    }
    return { result: result.value };
  }

  async [V2_QUERY_TYPES.GRAPHQL](
    clientState: ClientStateRoot,
    query: V2Query,
    fullRequest: V2Request,
    bindingsStack: CachedBindings[]
  ) {
    const q = query as QueryGraphQL;
    // GraphQL queries cannot be resolved by the SDK, but
    // subqueries passed in the 'values' object may need to be
    // resolved before the parent graphql query is submitted to the API.
    const values = q.values || {};
    for (let valueId in values) {
      const currentValue = values[valueId];
      if (isDataReference(currentValue)) {
        const queryResult = await this.evaluate(
          currentValue,
          clientState,
          fullRequest,
          bindingsStack
        );
        // it doesn't matter if the datareference was partially or fully resolved,
        // either way, it will be the value sent with the gql query
        values[valueId] = queryResult.value as DataReference | Literal;
      }
    }
    return {
      // graphql is always processed by the API, the SDK can only return a partially evaluated
      // result in which the 'values' object's contents is (partially) evaluated.
      partialResult: Object.assign({}, query, Object.keys(values).length > 0 ? { values } : null),
    };
  }

  async [V2_QUERY_TYPES.CONDITION_EVALUATION](
    clientState: ClientStateRoot,
    query: V2Query,
    fullRequest: V2Request,
    bindingsStack: CachedBindings[],
    traceState?: TraceState
  ): Promise<QueryExecutionResult> {
    const q = query as QueryEvaluateCondition;
    const evaluation = await this.evaluate(
      q.expression,
      clientState,
      fullRequest,
      bindingsStack,
      traceState
    );
    // evaluation completed
    if (isRight(evaluation)) {
      // value can be any literal; map to boolean using truthy values as true
      return { result: q.preserveValue === true ? evaluation.value : !!evaluation.value };
    }
    // evaluation could not complete, but may be different ConditionalExpression than original
    // if some arguments could be evaluated or replaced
    return {
      partialResult: {
        ...q,
        expression: evaluation.value,
      },
    };
  }

  static filterCurrent(productViews: ProductView[], fullRequest: V2Request): ProductView[] {
    const currentlViewedProductIds = new Set(
      fullRequest.events
        ?.filter(({ type }) => type === V2_EVENT_TYPES.PRODUCT_VIEW)
        .map(ev => (ev as EventProductView).productId)
    );
    return productViews.filter(
      ({ product: { productId } }) => !currentlViewedProductIds.has(productId)
    );
  }

  static mergeVariants(productViews: ProductView[]): ProductView[] {
    const seenProductIds = new Set();
    return productViews.reduce((list, view) => {
      const {
        product: { productId },
      } = view;
      if (!seenProductIds.has(productId)) {
        seenProductIds.add(productId);
        list.push(view);
      }
      return list;
    }, [] as ProductView[]);
  }

  async processQuery(
    clientState: ClientStateRoot,
    query: V2Query,
    fullRequest: V2Request,
    bindingsStack: CachedBindings[] = [],
    parentTraceState?: TraceState
  ): Promise<QueryExecutionResult> {
    if (!(query.type in this)) {
      // query cannot be evaluated, no need to add anything to parentTraceContext
      return { partialResult: query };
    }
    let traceState: TraceState | undefined = undefined;
    if (
      parentTraceState &&
      tracingEnabled(parentTraceState, 'tracedQueryTypes', query.type) &&
      tracingEnabled(parentTraceState, 'tracedExecutionEnvironments', ExecutionEnvironment.sdk)
    ) {
      traceState = {
        ...parentTraceState,
        context: createTraceQueryContext(parentTraceState, query, ExecutionEnvironment.sdk),
      };
    }
    let result: QueryExecutionResult;
    try {
      result = await (this as V2ProcessorBase)[query.type]!.call(
        this,
        clientState,
        query,
        fullRequest,
        bindingsStack,
        traceState
      );
    } catch (e) {
      result = {
        error: {
          message: e.message,
          stack: e.stack,
          code: ERROR_CODES.CLIENT_REQUEST_PROCESSOR_FAILED,
        },
      };
    }
    if (traceState) {
      traceState.context.exitTimestamp = getTraceTimestamp(traceState);
      (traceState.context as TraceQueryContext).result = result;
    }
    return result;
  }
}
